import { useState } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PageTitleBar from "@/components/PageTitleBar";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ACL_RELATIONSHIPS from "acl-relationships";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import EmailTemplateService from "services/EmailTemplate";
import {
  EMAIL_TEMPLATES_PATH,
  emailTemplateQueryKey,
  storeSavedTemplate,
} from "routes/email/emailTemplateCache";
import GeneralEmailTemplate from "routes/email/forms/GeneralEmailTemplate";
import GraphicalEmailTemplate from "routes/email/forms/GraphicalEmailTemplate";
import HtmlEmailTemplate from "routes/email/forms/HtmlEmailTemplate";
import RenderEmailTemplateList from "routes/email/forms/RenderEmailTemplateList";

const TABS = [
  { value: "general", label: "General" },
  { value: "graphical", label: "Graphical" },
  { value: "html", label: "HTML" },
  { value: "render", label: "Render" },
];

const PAGE_TITLES = {
  create: "New Email Template",
  clone: "Clone Email Template",
  edit: "Edit Email Template",
};

/**
 * Full-page email template editor. Each tab saves on its own, in order:
 * General creates the record, Graphical and HTML write its body, and Render
 * records the providers it is offered for. Until the record exists only the
 * General tab is available.
 */
const EmailTemplate = ({ mode }) => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOverwriteDialogOpen, setIsOverwriteDialogOpen] = useState(false);

  const isEdit = mode === "edit";
  const cloneId = searchParams.get("clone");
  const isClone = !isEdit && !!cloneId;
  const sourceId = isEdit ? id : cloneId;
  const formMode = isEdit ? "edit" : isClone ? "clone" : "create";

  const requestedTab = searchParams.get("tab");
  const activeTab =
    isEdit && TABS.some(tab => tab.value === requestedTab)
      ? requestedTab
      : "general";

  const canWrite = useAuthorized([
    isEdit
      ? ACL_RELATIONSHIPS.emailTemplates.edit
      : ACL_RELATIONSHIPS.emailTemplates.create,
  ]);

  const {
    data: template,
    isSuccess: templateLoaded,
    isError: templateError,
    isFetchedAfterMount,
  } = useQuery({
    queryKey: emailTemplateQueryKey(sourceId),
    queryFn: () => EmailTemplateService.getEmailTemplate(sourceId),
    enabled: !!sourceId,
    retry: false,
  });

  usePageTitle(isEdit ? template?.name : PAGE_TITLES[formMode]);

  // Replace, not push: tab switches must not stack history entries, so the
  // browser's back button leaves the editor instead of stepping through tabs.
  const showTab = tab =>
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      },
      { replace: true }
    );

  const handleTabChange = tab => {
    if (activeTab === "html" && tab === "graphical") {
      setIsOverwriteDialogOpen(true);
      return;
    }
    showTab(tab);
  };

  const confirmOverwrite = () => {
    setIsOverwriteDialogOpen(false);
    showTab("graphical");
  };

  const returnToList = () => navigate(EMAIL_TEMPLATES_PATH);

  const handleGeneralSaved = async saved => {
    await storeSavedTemplate(queryClient, saved);
    toast.success(isEdit ? "Email template updated" : "Email template created");
    if (isEdit) {
      showTab("graphical");
    } else {
      navigate(`${EMAIL_TEMPLATES_PATH}/${saved.id}?tab=graphical`, {
        replace: true,
      });
    }
  };

  const handleGraphicalSaved = async saved => {
    await storeSavedTemplate(queryClient, saved);
    toast.success("Email template updated");
    showTab("html");
  };

  const handleHtmlSaved = async (saved, { continueToNext }) => {
    await storeSavedTemplate(queryClient, saved);
    toast.success("Email template updated");
    if (continueToNext) showTab("render");
  };

  const handleRenderSaved = async saved => {
    await storeSavedTemplate(queryClient, saved);
    toast.success("Email template saved");
    returnToList();
  };

  if (!canWrite) {
    return <Navigate to={EMAIL_TEMPLATES_PATH} replace />;
  }

  if (templateError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <p>{"Couldn't load this email template. Please try again."}</p>
        <Link to={EMAIL_TEMPLATES_PATH}>Back to Email Templates</Link>
      </div>
    );
  }

  // isFetchedAfterMount, not just isSuccess: a cached entry resolves
  // synchronously as successful, and react-hook-form latches defaultValues at
  // each tab form's first render. Mounting on that payload would strand the
  // form on pre-save values, and saving would write them back over a newer
  // edit.
  if (sourceId && !(templateLoaded && isFetchedAfterMount)) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <Spinner />
      </div>
    );
  }

  const tabProps = { template, onCancel: returnToList };

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-center tw:font-sans tw:text-(--color-text-primary)">
      <div className="tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        <PageTitleBar
          backTo={EMAIL_TEMPLATES_PATH}
          backLabel="Back to Email Templates"
          title={PAGE_TITLES[formMode]}
          subtitle={isEdit ? template.name : undefined}
        />
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList variant="line">
            {TABS.map(tab => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                disabled={!isEdit && tab.value !== "general"}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {!isEdit && (
            <p className="tw:m-0 tw:text-sm tw:text-(--color-text-muted)">
              Save the general details to continue to the Graphical, HTML, and
              Render tabs.
            </p>
          )}
          <TabsContent value="general" className="tw:pt-2">
            <GeneralEmailTemplate
              {...tabProps}
              mode={formMode}
              onSaved={handleGeneralSaved}
            />
          </TabsContent>
          {isEdit && (
            <>
              <TabsContent value="graphical" className="tw:pt-2">
                <GraphicalEmailTemplate
                  {...tabProps}
                  onSaved={handleGraphicalSaved}
                />
              </TabsContent>
              <TabsContent value="html" className="tw:pt-2">
                <HtmlEmailTemplate {...tabProps} onSaved={handleHtmlSaved} />
              </TabsContent>
              <TabsContent value="render" className="tw:pt-2">
                <RenderEmailTemplateList
                  {...tabProps}
                  onSaved={handleRenderSaved}
                />
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>

      <AlertDialog
        open={isOverwriteDialogOpen}
        onOpenChange={setIsOverwriteDialogOpen}
      >
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to the Graphical tab?</AlertDialogTitle>
            <AlertDialogDescription>
              Saving in the Graphical tab regenerates the HTML from the design
              and overwrites any changes made in the HTML tab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel variant="outline">
              Stay on HTML
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmOverwrite}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmailTemplate;
